'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import TrailDetailsOverviewLink from '@alltrails/amplitude/enums/TrailDetailsOverviewLink';
import logTrailDetailsOverviewLinkClicked from '@alltrails/amplitude/events/logTrailDetailsOverviewLinkClicked';
import logTrailDetailsPlanYourVisitShowMoreClicked from '@alltrails/amplitude/events/logTrailDetailsPlanYourVisitShowMoreClicked';
import logTrailDetailsPlanYourVisitViewed from '@alltrails/amplitude/events/logTrailDetailsPlanYourVisitViewed';
import useUser from '@alltrails/context/hooks/useUser';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import TrailWheelchair from '@alltrails/denali/icons/TrailWheelchair';
import getSortedGroupedTags, { tagGroupPriorityOrder } from '@alltrails/trails/utils/getSortedGroupedTags';
import type GroupedTag from '@alltrails/types/GroupedTag';
import type Tag from '@alltrails/types/Tag';
import type TrailDetails from '@alltrails/types/TrailDetails';
import cardStyles from '../../components/CardModule/CardModule.module.scss';
import GroupTag from './GroupTag';
import HelpfulLinkTag from './HelpfulLinkTag';
import styles from './PlanYourVisit.module.scss';

const PlanYourVisitModal = dynamic(() => import('./PlanYourVisitModal'));
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type PlanYourVisitProps = {
  isBot?: boolean;
  groupedTags: GroupedTag[];
  trailDetails: TrailDetails;
  trailId: number;
};

const PlanYourVisit = ({ isBot = false, groupedTags, trailDetails, trailId }: PlanYourVisitProps) => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(ref, { threshold: 1 });
  const planYourVisitRef = useRef<boolean>(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isShowMoreModalOpen, setIsShowMoreModalOpen] = useState(false);
  const [initialModal, setInitialModal] = useState<string>();
  const isMobile = useIsMobileSizedScreen();
  const user = useUser();
  const showAccessibilityLink = !isMobile && !!trailDetails.accessibility;

  useEffect(() => {
    if (isVisible && !planYourVisitRef.current) {
      planYourVisitRef.current = true;
      logTrailDetailsPlanYourVisitViewed({ trail_id: String(trailId) });
    }
  }, [isVisible, trailId]);

  const tags = groupedTags.flatMap(group =>
    group.tags.map((tag: Tag) => ({
      name: tag.name,
      uid: tag.uid
    }))
  );

  if (isBot) {
    return (
      <div className={styles.tagsContainer}>
        {tags &&
          tags.map(tag => (
            <div key={tag.uid} className={styles.tagName}>
              {tag.name}
            </div>
          ))}
      </div>
    );
  }

  const onShowMoreClick = () => {
    setInitialModal(undefined);
    logTrailDetailsPlanYourVisitShowMoreClicked({ trail_id: String(trailId) });
    if (!user) {
      setIsSignUpModalOpen(true);
      return;
    }

    setIsShowMoreModalOpen(true);
  };

  const onHelpfulLinkClick = (id: string, trailDetailsOverviewLink?: TrailDetailsOverviewLink) => {
    setInitialModal(id);
    if (trailDetailsOverviewLink) {
      logTrailDetailsOverviewLinkClicked({ overview_link_clicked: trailDetailsOverviewLink, trail_id: trailDetails.id });
    }
    if (!user) {
      setIsSignUpModalOpen(true);
      return;
    }

    setIsShowMoreModalOpen(true);
  };

  const maxDesktopTags = [showAccessibilityLink].reduce((acc, curr) => acc - (curr ? 1 : 0), 6);
  const maxTags = isMobile ? 3 : maxDesktopTags;
  const sortedTags = groupedTags && groupedTags.length > 0 ? getSortedGroupedTags(groupedTags, maxTags) : null;

  const getSortedGroups = (tagsData: GroupedTag[]): GroupedTag[] => {
    return tagsData.sort((a, b) => {
      const indexA = tagGroupPriorityOrder.indexOf(a.id);
      const indexB = tagGroupPriorityOrder.indexOf(b.id);
      return indexA - indexB;
    });
  };

  const sortedGroups = getSortedGroups(groupedTags);
  return (
    <div ref={ref} className={classNames(cardStyles.card, cardStyles.noBackgroundOnMobile)}>
      <div className={classNames(cardStyles.inner, cardStyles.adjustedPadding)}>
        <Heading as="h2" className={classNames(cardStyles.cardTitle, cardStyles.planYourVisitMobileTitle)} size="300">
          <FormattedMessage defaultMessage="Plan your visit" />
        </Heading>
        {sortedTags && (
          <div className={styles.tagsContainer}>
            {showAccessibilityLink && (
              <HelpfulLinkTag
                icon={TrailWheelchair}
                iconEmphasis={isMobile ? 'base' : 'weak'}
                id="accessibility"
                label={intl.formatMessage({ defaultMessage: 'Accessibility' })}
                onClick={id => onHelpfulLinkClick(id, 'accessibility')}
              />
            )}
            {sortedTags?.map(tag => (
              <GroupTag key={tag.uid} tag={tag} isLightBackground={!isMobile} />
            ))}
          </div>
        )}
        <div className={styles.container}>
          <div className={classNames(styles.buttonContainer, styles.showMore)}>
            <div className={styles.action}>
              <Button
                onClick={onShowMoreClick}
                text={<FormattedMessage defaultMessage="Show more" />}
                size="md"
                className={styles.showMoreButton}
                variant={isMobile ? 'secondary' : 'ghost'}
                elevated={!isMobile}
              />
            </div>
          </div>
        </div>
      </div>
      {isSignUpModalOpen && (
        <SignUpModal
          trigger="Plan Your Visit Show More"
          isOpen={isSignUpModalOpen}
          closeModal={() => setIsSignUpModalOpen(false)}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
          }}
          upsellTrigger="Plan Your Visit Show More"
        />
      )}
      {isShowMoreModalOpen && (
        <PlanYourVisitModal
          groups={sortedGroups}
          initialModal={initialModal}
          onCloseRequest={() => {
            setIsShowMoreModalOpen(false);
            setInitialModal(undefined);
          }}
          trailDetails={trailDetails}
        />
      )}
    </div>
  );
};

export default PlanYourVisit;
